import { supabase } from '../supabaseClient';

/**
 * Fetch all resources from the database
 * @returns {Promise<Array>} Array of all resources with id, name, capacity
 * @throws {Error} If database query fails
 */
export const fetchAllResources = async () => {
  const { data, error } = await supabase
    .from('Resource')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
};

/**
 * Create a new resource
 * @param {string} name - Resource name
 * @param {number} capacity - Resource capacity
 * @returns {Promise<Object>} Created resource object
 * @throws {Error} If name or capacity is missing, or if insert fails
 */
export const createResource = async (name, capacity) => {
  if (!name || capacity == null) {
    throw new Error('Resource name and capacity are required');
  }

  const { data, error } = await supabase
    .from('Resource')
    .insert({
      name,
      capacity,
    })
    .select();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

/**
 * Update resource capacity
 * @param {string} resourceId - Resource ID
 * @param {number} capacity - New capacity value
 * @returns {Promise<Object>} Updated resource object
 * @throws {Error} If resourceId or capacity is missing, or if update fails
 */
export const updateResourceCapacity = async (resourceId, capacity) => {
  if (!resourceId || capacity == null) {
    throw new Error('Resource ID and capacity are required');
  }

  const { data, error } = await supabase
    .from('Resource')
    .update({ capacity })
    .eq('id', resourceId)
    .select();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

/**
 * Update resource name
 * @param {string} resourceId - Resource ID
 * @param {string} name - New name value
 * @returns {Promise<Object>} Updated resource object
 * @throws {Error} If resourceId or name is missing, or if update fails
 */
export const updateResourceName = async (resourceId, name) => {
  if (!resourceId || !name) {
    throw new Error('Resource ID and name are required');
  }

  const { data, error } = await supabase
    .from('Resource')
    .update({ name })
    .eq('id', resourceId)
    .select();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

/**
 * Delete a resource
 * @param {string} resourceId - Resource ID to delete
 * @returns {Promise<void>}
 * @throws {Error} If resourceId is missing, or if delete fails
 */
export const deleteResource = async (resourceId) => {
  if (!resourceId) {
    throw new Error('Resource ID is required');
  }

  const { error } = await supabase
    .from('Resource')
    .delete()
    .eq('id', resourceId);

  if (error) {
    throw new Error(error.message);
  }
};
